import React, { useState } from "react";
import {
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
} from "@mui/material";
import TextInput, { BootstrapInput } from "../../../Component/UI/TextInput/TextInput";
import classes from "../settings.module.css";

const CategoryMonitoringForm = ({
  formValues,
  setFormValues,
  isUpdateMode,
  markets,
  llms,
  promptTemplates,
  handleCloseModal,
  onSubmit,
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const handleChange = (field) => (e) => {
    setFormValues((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleLlmIdsChange = (e) => {
    const value = e.target.value;
    setFormValues((prev) => ({
      ...prev,
      llm_ids: typeof value === "string" ? value.split(",").map(Number) : value,
    }));
  };

  const handleTemplateChange = (e) => {
    const template = promptTemplates?.find((t) => t.id === e.target.value);
    if (template) {
      setSelectedTemplateId(template.id);
      setFormValues((prev) => ({ ...prev, visibility_prompt: template.visibility_prompt }));
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    onSubmit(formValues);
  };

  const selectedMarketName = markets?.find((m) => m.id === formValues.market_id)?.name;

  const getLlmLabel = (llm) => llm.display_name || llm.name;
  const selectedLlmLabels = (formValues.llm_ids || [])
    .map((id) => getLlmLabel(llms?.find((l) => l.id === id) || {}))
    .filter(Boolean)
    .join(", ");

  return (
    <form onSubmit={handleFormSubmit}>
      <div className={classes.divContainer}>
        {isUpdateMode ? (
          <>
            <TextInput label="Category" value={formValues.category || ""} disabled fullWidth />
            <TextInput label="Market" value={selectedMarketName || ""} disabled fullWidth />
          </>
        ) : (
          <>
            <TextInput
              label="Category"
              fullWidth
              required
              value={formValues.category || ""}
              onChange={handleChange("category")}
            />
            <FormControl variant="standard" size="small" fullWidth>
              <InputLabel shrink>Market</InputLabel>
              <Select
                value={formValues.market_id || ""}
                onChange={handleChange("market_id")}
                input={<BootstrapInput />}
                displayEmpty
                required
              >
                <MenuItem value="" disabled>
                  Select a market
                </MenuItem>
                {markets?.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        <TextInput
          label="Brands (comma-separated)"
          fullWidth
          required
          value={formValues.brands || ""}
          onChange={handleChange("brands")}
          placeholder="e.g. Ozempic, Wegovy, Rybelsus"
        />

        <FormControl variant="standard" size="small" fullWidth>
          <InputLabel shrink>LLMs</InputLabel>
          <Select
            multiple
            value={formValues.llm_ids || []}
            onChange={handleLlmIdsChange}
            input={<BootstrapInput />}
            displayEmpty
            required
            renderValue={() => selectedLlmLabels || "Select LLMs"}
          >
            {llms?.map((llm) => (
              <MenuItem key={llm.id} value={llm.id}>
                <Checkbox checked={(formValues.llm_ids || []).includes(llm.id)} />
                <ListItemText primary={getLlmLabel(llm)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!!promptTemplates?.length && (
          <FormControl variant="standard" size="small" fullWidth>
            <InputLabel shrink>Load Prompt From Template</InputLabel>
            <Select
              value={selectedTemplateId}
              onChange={handleTemplateChange}
              input={<BootstrapInput />}
              displayEmpty
              required
            >
              <MenuItem value="" disabled>
                Select a template to load its prompt
              </MenuItem>
              {promptTemplates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                  {t.is_default ? " ★" : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Commented out for now: visibility_prompt is set entirely via the template
            picker above until this is re-enabled.
        <TextInput
          label="Visibility Prompt"
          fullWidth
          required
          multiline
          rows={5}
          value={formValues.visibility_prompt || ""}
          onChange={handleChange("visibility_prompt")}
        /> */}

        <div className={classes.buttonDiv}>
          <Button type="submit" variant="contained">
            {isUpdateMode ? "Update" : "Add"}
          </Button>
          <Button variant="outlined" onClick={handleCloseModal}>
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CategoryMonitoringForm;
